import React from 'react';
import { Link } from 'react-router-dom';

const GRID_BG = {
  backgroundImage:
    'linear-gradient(rgba(201,168,76,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(201,168,76,0.12) 1px, transparent 1px)',
  backgroundSize: '60px 60px',
};

const CAPABILITIES = [
  {
    title: 'Real Property Disposal Project (RPDP)',
    desc: 'We establish and maintain dynamic, audit-ready asset inventories tracking crucial data points — site names, property IDs, fiscal requirements, and documentation statuses — ensuring complete regulatory visibility at every stage.',
  },
  {
    title: 'Regulatory Liaison & Coordination',
    desc: 'Seamless alignment of disposal timelines between agency Real Property Management Divisions (RPMD), Line Offices (LO), and Safety & Environmental Compliance Offices (SECO) to eliminate interagency friction.',
  },
  {
    title: 'Mandatory Compliance Auditing',
    desc: 'Expert navigation of strict statutory frameworks including the McKinney-Vento Homeless Assistance Act, National Environmental Protection Act (NEPA), National Historic Preservation Act (NHPA), and applicable State and Local Regulations.',
  },
];

const PIPELINE = [
  {
    step: '1',
    title: 'Excess Property Identification',
    subtitle: 'Asset Evaluation',
    desc: 'We assist Line Offices with annual existence testing and data validation to identify properties with no commercial value or those where maintenance costs exceed projected sale proceeds.',
  },
  {
    step: '2',
    title: 'Federal Transfer Screening',
    subtitle: 'Internal Utilization',
    desc: 'Before an asset leaves the federal government, we coordinate with the GSA to screen and offer excess property to other federal agencies with active program needs — maximizing internal utilization.',
  },
  {
    step: '3',
    title: 'Surplus & Public Benefit Conveyance',
    subtitle: 'Community Realignment',
    desc: 'If no federal need is found, the property is declared surplus. We manage compliance for Homeless Conveyance (via HUD priority screening) and Public Benefit Conveyances (PBC), enabling communities to acquire properties for educational, park, or civic use.',
  },
  {
    step: '4',
    title: 'Competitive Sale or Compliant Demolition',
    subtitle: 'Final Resolution',
    desc: 'When public conveyance is not utilized, we facilitate GSA Negotiated Sales with local governments or Public Sales via competitive auction. If removal is required, we secure GSA clearances and manage fully compliant Demolition Procedures.',
  },
];

const RealProperty = () => {
  return (
    <div className="pt-20">

      {/* Page header */}
      <section className="relative bg-navy-800 py-20 overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gold-500/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/3" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-slate-400 mb-6">
            <Link to="/" className="hover:text-gold-400 transition-colors">Home</Link>
            <span>/</span>
            <span className="text-gold-400">Federal Real Property Disposal</span>
          </nav>
          <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-4">Service Line</p>
          <h1 className="text-4xl md:text-5xl font-black text-white font-heading leading-tight max-w-3xl">
            Federal Real Property Disposal
          </h1>
          <p className="text-slate-300 text-lg mt-6 max-w-2xl leading-relaxed">
            DTX serves as Subject Matter Experts (SMEs) guiding federal agencies through the compliant,
            end-to-end lifecycle of real property disposal under Federal Management Regulation (FMR) § 102-75 —
            from undeveloped land and office buildings to specialized military holdings and industrial warehouses.
          </p>
        </div>
      </section>

      {/* Past performance callout */}
      <section className="bg-gold-500/10 border-y border-gold-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm font-semibold text-navy-800">
            <span>Proven Performance:</span>
            <span className="text-slate-600 font-normal">NOAA / National Weather Service (PRIME, 2016–2027)</span>
            <span className="text-gold-500">·</span>
            <span className="text-slate-600 font-normal">Engineering Support & Real Property Disposal (Per NEPA)</span>
            <span className="text-gold-500">·</span>
            <span className="text-slate-600 font-normal">Space Management Services</span>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="bg-slate-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">Our Capabilities</p>
            <h2 className="text-4xl font-black text-navy-800 font-heading">Core Capabilities</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {CAPABILITIES.map(({ title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:border-gold-200 hover:shadow-md transition-all">
                <div className="w-10 h-10 bg-gold-500/10 border border-gold-200 rounded-xl flex items-center justify-center mb-5">
                  <div className="w-3 h-3 bg-gold-500 rounded-full" />
                </div>
                <h3 className="text-base font-bold text-navy-800 font-heading mb-3">{title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Disposition Pipeline */}
      <section className="bg-white py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-gold-600 text-sm font-bold uppercase tracking-widest mb-4">GSA Priority Hierarchy</p>
            <h2 className="text-4xl font-black text-navy-800 font-heading">The Federal Disposition Pipeline</h2>
            <p className="text-slate-500 mt-4 max-w-xl mx-auto">
              Federal property disposition is managed on a case-by-case basis. We guide assets through the
              mandatory GSA priority hierarchy to ensure the highest and best use at every stage.
            </p>
          </div>
          <div className="space-y-6">
            {PIPELINE.map(({ step, title, subtitle, desc }) => (
              <div key={step} className="flex gap-6 bg-slate-50 rounded-2xl p-8 border border-gray-100">
                <div className="shrink-0">
                  <div className="w-12 h-12 bg-navy-800 rounded-xl flex items-center justify-center">
                    <span className="text-gold-500 font-black text-lg">{step}</span>
                  </div>
                </div>
                <div>
                  <div className="flex flex-wrap items-baseline gap-2 mb-2">
                    <h3 className="font-bold text-navy-800 font-heading">{title}</h3>
                    <span className="text-gold-600 text-xs font-bold uppercase tracking-wider">{subtitle}</span>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why it matters */}
      <section className="bg-navy-800 py-16 relative overflow-hidden">
        <div className="absolute inset-0" style={GRID_BG} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
            <p className="text-gold-500 text-sm font-bold uppercase tracking-widest mb-4">Why It Matters</p>
            <h2 className="text-3xl font-black text-white font-heading mb-5">
              Real Property Optimization Serves Communities
            </h2>
            <p className="text-slate-300 leading-relaxed max-w-2xl mx-auto mb-8">
              Safely and efficiently offloading underutilized federal real estate does more than cut agency
              holding costs — it directly expands local employment opportunities, creates vital housing, and
              establishes new public parks and educational centers that revitalize communities.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-gold-500 hover:bg-gold-400 text-navy-900 px-8 py-4 rounded-xl font-bold transition-colors shadow-lg"
            >
              Discuss Your Real Property Needs
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default RealProperty;
